import {
  AppBar,
  Box,
  Breadcrumbs,
  Link,
  Tabs,
  Typography,
} from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import clsx from "clsx";
import React from "react";

import { Link as RouterLink } from "react-router-dom";

import Navigation from "../Navigation";
import BlockerStalenessBanner from "./BlockerStalenessBanner";
import useWTMPageStyles from "./WebTrackerManagerPage/useWTMPageStyles";
import ListTab from "./WebTrackerManagerPage/ListTab";
import PHIAllowListTab from "./WebTrackerManagerPage/PHIAllowListTab";
import { theme } from "../theme";
import LinkTab from "./LinkTab";
import useWTMURLs from "./useWTMURLs";
import useFeatureFlags from "../hooks/useFeatureFlag";
import OverviewDataContextProvider from "./OverviewDataContextProvider";

export enum WTMTab {
  TrackerList = 0,
  PHIAllowList = 1,
}

type WTMNavSegmentProps = {
  tabValue: WTMTab;
};

function WTMNavSegment({ tabValue }: WTMNavSegmentProps) {
  const urls = useWTMURLs();

  switch (tabValue) {
    case WTMTab.TrackerList:
      return (
        <RouterLink to={urls.trackersOverview}>
          <Typography variant="overline">Tracking Tools</Typography>
        </RouterLink>
      );

    case WTMTab.PHIAllowList:
      return urls.phiAllowlist !== undefined ? (
        <RouterLink to={urls.phiAllowlist}>
          <Typography variant="overline">PHI Allowlist</Typography>
        </RouterLink>
      ) : null;
  }
}

export type WebTrackerManagerPageProps = {
  tabValue: WTMTab;
};

function PageDescription({ tabValue }: WebTrackerManagerPageProps) {
  const flags = useFeatureFlags();

  if (!flags("unifiedDestinationsUI")) {
    return (
      <Typography component="h1" gutterBottom variant="h3">
        Detect and manage tracking tools.
      </Typography>
    );
  }

  switch (tabValue) {
    case WTMTab.TrackerList:
      return (
        <>
          <Typography component="h1" gutterBottom variant="h3">
            Manage HIPAA compliance for tracking tools.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Monitor your site for tracking tools, and manage tracking tools
            you've already set up as active Freshpaint destinations.
          </Typography>
        </>
      );

    case WTMTab.PHIAllowList:
      return (
        <>
          <Typography component="h1" gutterBottom variant="h3">
            PHI Allowlist Overview
          </Typography>
          <Typography variant="body1" gutterBottom>
            Understand which properties are being sent to the active Freshpaint
            destinations in this environment.{" "}
            <Link
              href="https://documentation.freshpaint.io/integrations/hipaa-mode#destination-allowlist"
              target="_blank"
              rel="noreferrer"
            >
              Learn more about PHI Allowlists in our docs.
            </Link>
          </Typography>
        </>
      );
  }

  return null;
}

function WebTrackerManagerPage({ tabValue }: WebTrackerManagerPageProps) {
  const classes = useWTMPageStyles();
  const flag = useFeatureFlags();
  const urls = useWTMURLs();

  return (
    <Navigation className={clsx(classes.navigation)}>
      <BlockerStalenessBanner />

      <div className={classes.pageTitle}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className={clsx(classes.breadcrumb)}
        >
          <Typography variant="overline">Monitoring</Typography>
          <WTMNavSegment tabValue={tabValue} />
        </Breadcrumbs>
        <PageDescription tabValue={tabValue} />
      </div>

      {flag("unifiedDestinationsUI") && (
        <nav className={classes.tabNav}>
          <AppBar
            position="static"
            elevation={0}
            style={{
              background: theme.palette.grey[100],
            }}
          >
            <Tabs variant="fullWidth" value={tabValue} onChange={() => {}}>
              <LinkTab label="Tracking Tools" to={urls.trackersOverview} />
              <LinkTab label="PHI Allowlist" to={urls.phiAllowlist as string} />
            </Tabs>
          </AppBar>
        </nav>
      )}

      <OverviewDataContextProvider>
        <Box className={classes.grid}>
          {tabValue === WTMTab.TrackerList && <ListTab />}
          {tabValue === WTMTab.PHIAllowList && <PHIAllowListTab />}
        </Box>
      </OverviewDataContextProvider>
    </Navigation>
  );
}

export default WebTrackerManagerPage;
